import { Dialog, DialogSize, Button, GroupPanel } from "portal-components";
import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import translate, { DefaultStrings } from "../i18n/translate";
import { logout } from "../features/login";

// eslint-disable-next-line @typescript-eslint/no-var-requires
const classes = require("./offlineScreen.scss");

const defaultStrings = {
    title: "You are offline",
    tryCheckinge: "No Internet, you may try to:",
    solution1: "Check your router and your network cable.",
    solution2: "Reconnect to the Wi-Fi network.",
    solution3: "Check Proxy settings in your browser.",
    loggedInAlert: "You are logged in",
    loggedInMessage1: "You are offline but still logged in.",
    loggedInMessage2:
        "If you are not going back online, to improve security, we suggest to logout from your client-side session before closing this window.",
    logout: "Logout now",
};

export interface OfflineScreenProps {
    strings: DefaultStrings<typeof defaultStrings>;
    authenticated: boolean;
}

export const OfflineScreenComponent: React.FunctionComponent<OfflineScreenProps> = (props) => {
    const [offline, setOffline] = useState(window.navigator ? !window.navigator.onLine : false);
    const [logoutPerformed, setLogoutPerformed] = useState(false);
    const handleConnectionChanged = () => setOffline(!window.navigator?.onLine);
    const dispatch = useDispatch();

    useEffect(() => {
        window.addEventListener("offline", handleConnectionChanged);
        window.addEventListener("online", handleConnectionChanged);

        return () => {
            window.removeEventListener("offline", handleConnectionChanged);
            window.removeEventListener("online", handleConnectionChanged);
        };
    }, []);

    const canLogout = props.authenticated && !logoutPerformed;
    const handleLogout = () => {
        setLogoutPerformed(true);
        dispatch(logout({ endIdpSession: true }));
    };

    return (
        <Dialog title={props.strings.title} size={DialogSize.Large} canClose={false} open={offline}>
            <img
                className={classes.cloud}
                src="data:image/png;base64,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"
            />

            <p>{props.strings.tryCheckinge}</p>
            <ul>
                <li>{props.strings.solution1}</li>
                <li>{props.strings.solution2}</li>
                <li>{props.strings.solution3}</li>
            </ul>

            <GroupPanel when={canLogout} title={props.strings.loggedInAlert}>
                <p>{props.strings.loggedInMessage1}</p>
                <p>{props.strings.loggedInMessage2}</p>
                <br />
                <Button text={props.strings.logout} onClick={handleLogout} />
            </GroupPanel>
        </Dialog>
    );
};

OfflineScreenComponent.displayName = "OfflineScreenComponent";
OfflineScreenComponent.defaultProps = {
    strings: defaultStrings,
};

export default translate("OfflineScreenComponent")(OfflineScreenComponent);
